import { useCallback, useEffect, useMemo, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import { CSVLink } from 'react-csv';
import { format, subMonths, isValid, isWithinRange, startOfDay, endOfDay } from 'date-fns';
import { LoaderSmallCentralized } from '@components/Loader';
import { Tooltip } from '@components/Tooltip';
import { toast } from 'react-toastify';
import { TOAST_AUTO_CLOSE_TIME } from '@shared/constants';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import {
  createTimeTrackingManually,
  deleteTimeTrackingManually,
  getTimeTrackings,
  updateTimeTrackingManually,
} from './services';
import { RetroactiveTimeTracking, RetroactiveTrackingActivityType } from './types';
import {
  ACTIVITY_TYPES,
  csvReportHeaders,
  formatDateTime,
  formatDuration,
  getActivityTypeLabel,
  getCsvReportData,
  getEndTimeLabel,
  getTrackingTypeLabel,
} from './helpers';
import {
  ActionButton,
  BodyCell,
  EditSelect,
  HeaderCell,
  Placeholder,
  StyledTable,
  TableWrap,
  Title,
  Wrapper,
  colWidths,
  textFieldStyles,
  datePickerSx,
} from './styles';
import { TrackingType } from '@shared/TimeTracker/types';
import { mentorRoles } from '@shared/constants/roles';
import DurationInput from './DurationInput';
import DatePicker from '@components/DatePicker';

type Filter = {
  from: Date;
  to: Date;
};

const defaultFilter = { from: subMonths(new Date(), 1), to: new Date() };

const MentorTimeTracking = () => {
  const dispatch = useDispatch();
  const { id: userId } = useParams<{ id: string }>();
  const [records, setRecords] = useState<RetroactiveTimeTracking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [filter, setFilter] = useState<Filter>(defaultFilter);
  const [editDraft, setEditDraft] = useState<Partial<RetroactiveTimeTracking>>({});
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [newDraft, setNewDraft] = useState<Partial<RetroactiveTimeTracking>>({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { t } = useTranslation();

  const hasTableData = useMemo(() => !error && !!records.length, [error, records]);
  const hasVisibleTable = useMemo(
    () => !error && (!!records.length || isAddingRow),
    [error, records.length, isAddingRow],
  );

  const { userData } = useSelector((state: RootStateOrAny) => state.get('userPage').toJS()); // FIXME replace to typing store

  const {
    id: mentorId,
    enableTimeTracking,
    roles = [],
  } = useSelector((state: RootStateOrAny) => state.getIn(['app', 'authentication', 'user']).toJS()); // FIXME replace to typing store

  const isClinicalMentor = roles.some((roleId: string) => mentorRoles.includes(roleId));

  const isUserMentor = useMemo(
    () => userData?.mentor?.some((m: any) => m.id === mentorId),
    [userData?.mentor, mentorId],
  );

  const csvReportData = useMemo(() => getCsvReportData(records), [records]);

  const loadTimeTrackings = useCallback(
    async (filter: Filter = defaultFilter) => {
      if (!userId || !mentorId || !isClinicalMentor || !isUserMentor || !enableTimeTracking) {
        setRecords([]);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const data = await getTimeTrackings(userId, mentorId, filter.from.toISOString(), filter.to.toISOString());
        setRecords(data);
      } catch (e) {
        setError('Failed to load time tracking records.');
      } finally {
        setLoading(false);
      }
    },
    [mentorId, userId, isUserMentor, enableTimeTracking, isClinicalMentor],
  );

  useEffect(() => {
    loadTimeTrackings();
  }, [loadTimeTrackings]);

  const handleEdit = (record: RetroactiveTimeTracking) => {
    if (isAddingRow) return;
    setEditDraft({ ...record });
  };

  const handleCancel = () => {
    setEditDraft({});
  };

  const handleDraftChange = (
    field: keyof RetroactiveTimeTracking,
    value: RetroactiveTimeTracking[keyof RetroactiveTimeTracking],
  ) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewDraftChange = (
    field: keyof RetroactiveTimeTracking,
    value: RetroactiveTimeTracking[keyof RetroactiveTimeTracking],
  ) => {
    setNewDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddRow = () => {
    if (!userId || !mentorId || editDraft?.id || deletingId) return;
    setIsAddingRow(true);
    setNewDraft({
      userId,
      mentorId,
      trackingType: TrackingType.RETROACTIVE,
      activityType: RetroactiveTrackingActivityType.OTHER,
      retroactiveStartTime: new Date().toISOString(),
      retroactiveDuration: 0,
      retroactiveEndTime: '',
      comments: '',
    });
  };

  const handleCancelAdd = () => {
    setIsAddingRow(false);
    setNewDraft({});
  };

  const createNewRecord = async () => {
    setSaving(true);
    try {
      const created = await createTimeTrackingManually({
        userId,
        mentorId,
        trackingType: newDraft.trackingType || TrackingType.RETROACTIVE,
        activityType:
          (newDraft.activityType as RetroactiveTrackingActivityType) || RetroactiveTrackingActivityType.OTHER,
        comments: newDraft.comments || '',
        retroactiveStartTime: newDraft.retroactiveStartTime || new Date().toISOString(),
        retroactiveDuration: newDraft.retroactiveDuration || 0,
        retroactiveEndTime: newDraft.retroactiveEndTime || '',
      });

      if (isWithinFilterRange(created)) {
        setRecords((prev) => [created, ...prev]);
      }

      setIsAddingRow(false);
      setNewDraft({});
    } catch (e) {
      toast.error('Failed to create time tracking record.', {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const updateEditedRecord = async () => {
    setSaving(true);
    try {
      const updated = await updateTimeTrackingManually(editDraft);
      setRecords((prev) => prev.map((r) => (r.id === editDraft.id ? { ...r, ...editDraft, ...updated } : r)));
      setEditDraft({});
    } catch (e) {
      toast.error('Failed to update time tracking record.', {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editDraft?.id) return;

    const validationError = validation(editDraft);
    if (validationError) {
      toast.error(validationError, {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
      return;
    }

    if (!isWithinFilterRange(editDraft)) {
      dispatch(
        openPopUp(POPUP_TYPES.CONFIRM, {
          titleText: 'Update time tracking record',
          bodyText:
            "The record is outside the selected date range\n You won't see it in the list right away\n Do you want to save it anyway?",
          confirmText: 'Update',
          cancelText: 'Cancel',
          disableBackdrop: true,
          onConfirm: (confirm: boolean) => {
            if (!confirm) return;

            updateEditedRecord();
          },
        }),
      );
      return;
    }

    await updateEditedRecord();
  };

  const handleSaveNew = async () => {
    if (!userId || !mentorId || !isAddingRow) return;

    const validationError = validation(newDraft);
    if (validationError) {
      toast.error(validationError, {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
      return;
    }

    const isInFilterRange = isWithinFilterRange(newDraft);
    if (!isInFilterRange) {
      dispatch(
        openPopUp(POPUP_TYPES.CONFIRM, {
          titleText: 'Create time tracking record',
          bodyText:
            "The record is outside the selected date range\n You won't see it in the list right away\n Do you want to create it anyway?",
          confirmText: 'Create',
          cancelText: 'Cancel',
          disableBackdrop: true,
          onConfirm: (confirm: boolean) => {
            if (!confirm) return;

            createNewRecord();
          },
        }),
      );
      return;
    }

    await createNewRecord();
  };

  const handleDelete = async (record: RetroactiveTimeTracking) => {
    if (editDraft?.id || isAddingRow || saving || deletingId) return;
    const startTime = record.retroactiveStartTime || record.startTime;
    const startTimeLabel = formatDateTime(startTime);
    const endTimeLabel = getEndTimeLabel(startTime, record.retroactiveDuration || record.duration);
    const trackingTypeLabel = getTrackingTypeLabel(record.trackingType);
    const activityTypeLabel = getActivityTypeLabel(record.activityType);
    dispatch(
      openPopUp(POPUP_TYPES.CONFIRM, {
        titleText: `Delete time tracking record`,
        bodyText: `Are you sure you want to delete this time tracking record?\n${activityTypeLabel} - ${trackingTypeLabel}\n${startTimeLabel} - ${endTimeLabel}`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        disableBackdrop: true,
        onConfirm: async (confirm: boolean) => {
          if (!confirm) {
            setDeletingId(null);
            return;
          }

          setDeletingId(record.id);
          try {
            await deleteTimeTrackingManually(record.id);
            setRecords((prev) => prev.filter((r) => r.id !== record.id));
          } catch (e) {
            toast.error('Failed to delete time tracking record.', {
              autoClose: TOAST_AUTO_CLOSE_TIME,
              position: 'bottom-right',
            });
          } finally {
            setDeletingId(null);
          }
        },
      }),
    );
  };

  const handleReset = async (record: RetroactiveTimeTracking) => {
    setSaving(true);
    try {
      const resetValues = {
        id: record.id,
        userId: record.userId,
        mentorId: record.mentorId,
        trackingType: record.trackingType,
        activityType: record.activityType,
        retroactiveStartTime: null,
        retroactiveDuration: null,
        retroactiveEndTime: null,
      };
      const updated = await updateTimeTrackingManually(resetValues);
      setRecords((prev) => prev.map((r) => (r.id === record.id ? { ...r, ...updated } : r)));
    } catch (e) {
      toast.error('Failed to reset time tracking record.', {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFilterChange = (date: Date | null, key: 'from' | 'to') => {
    const newFilter = { ...filter, [key]: date };
    setFilter(newFilter);
    if (newFilter.from && newFilter.to && isValid(newFilter.from) && isValid(newFilter.to)) {
      loadTimeTrackings(newFilter);
    }
  };

  const isCanReset = (record: RetroactiveTimeTracking) => {
    if (record.trackingType !== TrackingType.STOPWATCH) return false;
    if (record.retroactiveStartTime && record.retroactiveStartTime !== record.startTime) return true;
    if (record.retroactiveDuration && record.retroactiveDuration !== record.duration) return true;
    if (record.retroactiveEndTime && record.retroactiveEndTime !== record.endTime) return true;
    return false;
  };

  const validation = (record: Partial<RetroactiveTimeTracking>) => {
    if (!record.retroactiveStartTime && !record.startTime) return 'Start time is required';
    if (!isValid(new Date(record.retroactiveStartTime || record.startTime!))) return 'Invalid start time';
    if (!record.retroactiveDuration && !record.duration) return 'Duration is required';
    if (
      (typeof record.retroactiveDuration !== 'number' || record.retroactiveDuration <= 0) &&
      (typeof record.duration !== 'number' || record.duration <= 0)
    )
      return 'Invalid duration';
    return false;
  };

  const isWithinFilterRange = (record: Partial<RetroactiveTimeTracking>) => {
    if (record.trackingType === TrackingType.RETROACTIVE && !record.retroactiveStartTime) return false;
    if (
      isWithinRange(
        format(record.retroactiveStartTime || record.startTime!, 'YYYY-MM-DD'),
        startOfDay(filter.from),
        endOfDay(filter.to),
      )
    )
      return true;
    return false;
  };

  if (!isClinicalMentor || !isUserMentor) {
    return <Title>Unauthorized</Title>;
  }

  if (!enableTimeTracking) {
    return <Title>Time tracking is disabled</Title>;
  }

  if (loading) {
    return <LoaderSmallCentralized />;
  }

  return (
    <Wrapper>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', marginBottom: 16 }}>
        <Title>{t('UI.timeTracking')}</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          From
          <DatePicker
            value={filter.from}
            onChange={(date) => handleFilterChange(date, 'from')}
            format="DD/MM/YYYY"
            views={['year', 'month', 'day']}
            disableFuture
            sx={{ ...datePickerSx, maxWidth: 120 }}
          />
          To
          <DatePicker
            value={filter.to}
            onChange={(date) => handleFilterChange(date, 'to')}
            format="DD/MM/YYYY"
            views={['year', 'month', 'day']}
            disableFuture
            sx={{ ...datePickerSx, maxWidth: 120 }}
          />
        </div>
        {!error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CSVLink
              data={csvReportData}
              onClick={() => {
                if (!records.length) {
                  return false;
                }
              }}
              headers={csvReportHeaders}
              filename={`${userData?.fullName}_time-tracking-report_${format(filter.from, 'YYYY-MM-DD')}_${format(filter.to, 'YYYY-MM-DD')}.csv`}
            >
              <ActionButton
                $variant="delete"
                aria-label="Download Report"
                disabled={!records.length}
                title="Download Report"
                style={{ width: 'auto', padding: '0 10px', backgroundColor: '#388e3c' }}
              >
                <DownloadIcon fontSize="small" />
                <span style={{ marginLeft: 6 }}>Download Report</span>
              </ActionButton>
            </CSVLink>
            <ActionButton
              $variant="edit"
              onClick={handleAddRow}
              disabled={isAddingRow || !!editDraft?.id || saving || !!deletingId}
              aria-label="Add row"
              title="Add row"
              style={{ width: 'auto', padding: '0 10px' }}
            >
              <AddIcon fontSize="small" />
              <span style={{ marginLeft: 6 }}>Add row</span>
            </ActionButton>
          </div>
        )}
      </div>
      {error && <Placeholder>{error}</Placeholder>}
      {hasVisibleTable && (
        <TableWrap>
          <StyledTable>
            <colgroup>
              <col style={{ width: colWidths.activityType }} />
              <col style={{ width: colWidths.trackingType }} />
              <col style={{ width: colWidths.activity }} />
              <col style={{ width: colWidths.comments }} />
              <col style={{ width: colWidths.startTime }} />
              <col style={{ width: colWidths.duration }} />
              <col style={{ width: colWidths.endTime }} />
              <col style={{ width: colWidths.actions }} />
            </colgroup>
            <thead>
              <tr>
                <HeaderCell>Activity Type</HeaderCell>
                <HeaderCell>Tracking Type</HeaderCell>
                <HeaderCell>Activity</HeaderCell>
                <HeaderCell>Comments</HeaderCell>
                <HeaderCell>Start Time</HeaderCell>
                <HeaderCell>
                  Duration <small>(HH:MM:SS)</small>
                </HeaderCell>
                <HeaderCell>End Time</HeaderCell>
                <HeaderCell>Actions</HeaderCell>
              </tr>
            </thead>
            <tbody>
              {isAddingRow && (
                <tr>
                  <BodyCell>
                    <EditSelect
                      value={newDraft.activityType ?? RetroactiveTrackingActivityType.OTHER}
                      onChange={(e) => handleNewDraftChange('activityType', e.target.value)}
                    >
                      {Object.entries(ACTIVITY_TYPES).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </EditSelect>
                  </BodyCell>
                  <BodyCell>{getTrackingTypeLabel(TrackingType.RETROACTIVE)}</BodyCell>
                  <BodyCell></BodyCell>
                  <BodyCell>
                    <TextField
                      multiline
                      fullWidth
                      value={newDraft.comments ?? ''}
                      onChange={(e) => handleNewDraftChange('comments', e.target.value)}
                      variant="outlined"
                      sx={textFieldStyles}
                    />
                  </BodyCell>
                  <BodyCell>
                    <DatePicker
                      value={newDraft.retroactiveStartTime ? new Date(newDraft.retroactiveStartTime) : null}
                      onChange={(date) => handleNewDraftChange('retroactiveStartTime', date)}
                      format="DD/MM/YYYY HH:mm"
                      ampm={false}
                      disableFuture
                      sx={datePickerSx}
                    />
                  </BodyCell>
                  <BodyCell>
                    <DurationInput
                      value={newDraft.retroactiveDuration || newDraft.duration || 0}
                      onChange={(value) => handleNewDraftChange('retroactiveDuration', value)}
                    />
                  </BodyCell>
                  <BodyCell>{getEndTimeLabel(newDraft.retroactiveStartTime, newDraft.retroactiveDuration)}</BodyCell>
                  <BodyCell>
                    <ActionButton
                      $variant="save"
                      onClick={handleSaveNew}
                      disabled={saving}
                      aria-label="Save row"
                      title="Save row"
                    >
                      {saving ? <HourglassEmptyIcon fontSize="small" /> : <SaveIcon fontSize="small" />}
                    </ActionButton>
                    <ActionButton
                      $variant="cancel"
                      onClick={handleCancelAdd}
                      disabled={saving}
                      aria-label="Cancel add"
                      title="Cancel add"
                    >
                      <CloseIcon fontSize="small" />
                    </ActionButton>
                  </BodyCell>
                </tr>
              )}
              {records.map((record) =>
                editDraft?.id === record.id ? (
                  <tr key={record.id}>
                    <BodyCell>
                      <EditSelect
                        value={editDraft.activityType ?? record.activityType}
                        onChange={(e) => handleDraftChange('activityType', e.target.value)}
                      >
                        {Object.entries(ACTIVITY_TYPES).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </EditSelect>
                    </BodyCell>
                    <BodyCell>{getTrackingTypeLabel(record.trackingType)}</BodyCell>
                    <BodyCell>
                      {!!record.activityName && (
                        <Tooltip content={record.activityName} placement="top" hasArrow={false} padding={8}>
                          <span>{record.activityName}</span>
                        </Tooltip>
                      )}
                    </BodyCell>
                    <BodyCell>
                      <TextField
                        multiline
                        fullWidth
                        value={editDraft.comments ?? ''}
                        onChange={(e) => handleDraftChange('comments', e.target.value)}
                        variant="outlined"
                        sx={textFieldStyles}
                      />
                    </BodyCell>
                    <BodyCell>
                      <DatePicker
                        value={
                          editDraft.retroactiveStartTime
                            ? new Date(editDraft.retroactiveStartTime)
                            : editDraft.startTime
                              ? new Date(editDraft.startTime)
                              : null
                        }
                        onChange={(date) => handleDraftChange('retroactiveStartTime', date)}
                        format="DD/MM/YYYY HH:mm"
                        ampm={false}
                        disableFuture
                        sx={datePickerSx}
                      />
                    </BodyCell>
                    <BodyCell>
                      <DurationInput
                        value={editDraft.retroactiveDuration || editDraft.duration || 0}
                        onChange={(value) => handleDraftChange('retroactiveDuration', value)}
                      />
                    </BodyCell>
                    <BodyCell>
                      {getEndTimeLabel(
                        editDraft.retroactiveStartTime || editDraft.startTime,
                        editDraft.retroactiveDuration || editDraft.duration,
                      )}
                    </BodyCell>
                    <BodyCell>
                      <ActionButton
                        $variant="save"
                        onClick={handleSave}
                        disabled={saving}
                        aria-label="Save changes"
                        title="Save changes"
                      >
                        {saving ? <HourglassEmptyIcon fontSize="small" /> : <SaveIcon fontSize="small" />}
                      </ActionButton>
                      <ActionButton
                        $variant="cancel"
                        onClick={handleCancel}
                        disabled={saving}
                        aria-label="Cancel edit"
                        title="Cancel edit"
                      >
                        <CloseIcon fontSize="small" />
                      </ActionButton>
                    </BodyCell>
                  </tr>
                ) : (
                  <tr key={record.id}>
                    <BodyCell>{getActivityTypeLabel(record.activityType)}</BodyCell>
                    <BodyCell>{getTrackingTypeLabel(record.trackingType)}</BodyCell>
                    <BodyCell>
                      {!!record.activityName && (
                        <Tooltip content={record.activityName} placement="top" hasArrow={false} padding={8}>
                          <span>{record.activityName}</span>
                        </Tooltip>
                      )}
                    </BodyCell>
                    <BodyCell>
                      {!!record.comments && (
                        <Tooltip content={record.comments} placement="top" hasArrow={false} padding={8}>
                          <span>{record.comments}</span>
                        </Tooltip>
                      )}
                    </BodyCell>
                    <BodyCell>{formatDateTime(record.retroactiveStartTime || record.startTime)}</BodyCell>
                    <BodyCell>{formatDuration(record.retroactiveDuration || record.duration)}</BodyCell>
                    <BodyCell>
                      {getEndTimeLabel(
                        record.retroactiveStartTime || record.startTime,
                        record.retroactiveDuration || record.duration,
                      )}
                    </BodyCell>
                    <BodyCell>
                      <ActionButton
                        $variant="edit"
                        onClick={() => handleEdit(record)}
                        aria-label="Edit row"
                        title="Edit row"
                      >
                        <EditIcon fontSize="small" />
                      </ActionButton>
                      <ActionButton
                        $variant="delete"
                        onClick={() => handleDelete(record)}
                        disabled={saving || !!editDraft?.id || isAddingRow || deletingId === record.id}
                        aria-label="Delete row"
                        title="Delete row"
                      >
                        {deletingId === record.id ? (
                          <HourglassEmptyIcon fontSize="small" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </ActionButton>
                      {isCanReset(record) && (
                        <ActionButton
                          $variant="reset"
                          onClick={() => handleReset(record)}
                          disabled={saving || !!editDraft?.id || isAddingRow || deletingId === record.id}
                          aria-label="Reset"
                          title="Reset"
                        >
                          <RestoreIcon fontSize="small" />
                        </ActionButton>
                      )}
                    </BodyCell>
                  </tr>
                ),
              )}
            </tbody>
          </StyledTable>
        </TableWrap>
      )}
      {!hasTableData && !isAddingRow && <Placeholder>No time tracking records found.</Placeholder>}
    </Wrapper>
  );
};

export default MentorTimeTracking;
